import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import CustomNotify from '../../../Notify';
import Button from '../../../Button';
import FormGroup from '../../../FormGroup';
import { useI18n } from 'utils/I18n/context';

// icons controlled via CSS classes 'eye'/'eye-slash'

export const SecurityPassword = ({ changePassword }) => {
    const { t } = useI18n();
    const [state, setState] = useState({
        loading: false,
        errors: {},
        curPassword: '',
        newPassword: '',
        confirmationPassword: '',
    });

    const onSubmit = useCallback(
        (event) => {
            event.preventDefault();
            if (state.newPassword !== state.confirmationPassword) {
                setState((prev) => ({
                    ...prev,
                    errors: { ...prev.errors, new_password: ["Passwords don't match"] },
                }));
                return;
            }
            setState((prev) => ({ ...prev, loading: true }));
            changePassword(
                { ...state },
                () => {
                    CustomNotify(t.notyMessages?.update || 'Password updated successfully');
                    setState((prev) => ({ ...prev, loading: false, curPassword: '', newPassword: '', confirmationPassword: '' }));
                },
                (errors) => setState((prev) => ({ ...prev, errors, loading: false }))
            );
        },
        [state, changePassword, t]
    );

    const onChange = useCallback((event) => {
        const { name, value } = event.target;
        setState((prev) => {
            const updatedErrors = { ...prev.errors };
            if (name === 'confirmationPassword') {
                if (value !== prev.newPassword) {
                    updatedErrors.new_password = ["The entered passwords don't match"];
                } else {
                    delete updatedErrors.new_password;
                }
            }
            return { ...prev, [name]: value, errors: updatedErrors };
        });
    }, []);

    const resetPassword = useCallback(() => {
        setState((prev) => ({ ...prev, curPassword: '', newPassword: '', confirmationPassword: '', errors: {} }));
    }, []);

    const toggleShowPassword = useCallback((event) => {
        const icon = event.target;
        const input = icon.closest('.password-container').querySelector('input');
        icon.classList.toggle('eye');
        icon.classList.toggle('eye-slash');
        input.type = input.type === 'password' ? 'text' : 'password';
    }, []);

    const staticNames = t.securityPage?.changePassword || {};
    const staticNamesActions = t.actions || {};

    return (
        <form className="form-password form" onSubmit={onSubmit}>
            <div className="form-body">
                <FormGroup
                    label={`${staticNames.currentPassword || 'Current Password'}:`}
                    labelClass="control-label"
                    fieldClass="password-container"
                    errors={state.errors.current_password}
                >
                    <input
                        id="curPassword"
                        className="form-control short-input"
                        type="password"
                        name="curPassword"
                        value={state.curPassword}
                        required
                        onChange={onChange}
                        disabled={state.loading}
                    />
                    <span className="password-icon eye" onClick={toggleShowPassword}></span>
                </FormGroup>
                <FormGroup
                    label={`${staticNames.password || 'New Password'}:`}
                    labelClass="control-label"
                    fieldClass="password-container"
                    errors={state.errors.new_password}
                >
                    <input
                        id="newPassword"
                        className="form-control short-input"
                        type="password"
                        name="newPassword"
                        value={state.newPassword}
                        required
                        onChange={onChange}
                        disabled={state.loading}
                    />
                    <span className="password-icon eye" onClick={toggleShowPassword}></span>
                </FormGroup>
                <FormGroup
                    label={`${staticNames.passwordConfirmation || 'Confirm Password'}:`}
                    labelClass="control-label"
                    fieldClass="password-container"
                    errors={state.errors.new_password_confirmation || state.errors.new_password}
                >
                    <input
                        id="confirmationPassword"
                        className="form-control short-input"
                        type="password"
                        name="confirmationPassword"
                        value={state.confirmationPassword}
                        required
                        onChange={onChange}
                        disabled={state.loading}
                    />
                    <span className="password-icon eye" onClick={toggleShowPassword}></span>
                </FormGroup>
            </div>
            <div className="form-footer">
                <FormGroup fieldClass="save-form">
                    <Button
                        type="button"
                        text={staticNamesActions.cancel || 'Cancel'}
                        extraClass={{ 'cancel-btn': true }}
                        onClick={resetPassword}
                        disabled={state.loading}
                    />
                    <Button
                        type="submit"
                        text={staticNamesActions.save || 'Save'}
                        extraClass={{ 'apply-btn': true }}
                        disabled={state.loading}
                        loading={state.loading}
                    />
                </FormGroup>
            </div>
        </form>
    );
};

SecurityPassword.propTypes = {
    changePassword: PropTypes.func.isRequired,
};

SecurityPassword.defaultProps = {
    changePassword: () => {},
};